// Sibling flavor navigation — fetch-and-swap (no page jump) — by Studio Lab
//
// The PDP flavor selector (blocks/groovi-flavor-selector.liquid) links each
// sibling flavor (custom.sibling_products) to its own PDP via a plain <a href>.
// A full navigation jumps the scroll and flashes. Instead we intercept the
// click and morph the product-information section in place using Horizon's own
// Section Rendering API (sectionRenderer) + morph — the same machinery combined
// listings use. Custom elements (variant-picker, media-gallery, sticky-add-to-
// cart) re-upgrade through the morph; scroll position is preserved.
//
// IMPORTANT (CLAUDE.md / DECISIONS): view transitions stay OFF
// (page_transition_enabled:false, transition_to_main_product:false). This does
// NOT enable them — it is a scoped fetch-and-swap, so the locked decision holds.

import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';

const LINK_SELECTOR = 'a.groovi-flavor__item[href]';

/**
 * True when the Kaching Bundles app is active on the current product. Detected by
 * its runtime API or its rendered widget (either is enough — the API can lag the
 * first paint, the widget can precede the API). Used to route bundle products to a
 * full navigation instead of the in-place morph, which corrupts the app's state.
 * @returns {boolean}
 */
function hasBundleWidget() {
  return !!(window.kachingBundlesApi || document.querySelector('.kaching-bundles__block, .kaching-bundles'));
}

// Never hijack navigation inside the Theme Editor — let the editor navigate
// normally so section reloads behave as the merchant expects.
if (!window.Shopify || !window.Shopify.designMode) {
  init();
}

function init() {
  document.addEventListener('click', (event) => {
    // Honour new-tab / modified clicks and anything already handled.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest(LINK_SELECTOR) : null;
    if (!link) return;

    const href = link.getAttribute('href');
    const section = link.closest('.shopify-section');
    if (!href || !section || !section.id) return; // fall through to normal navigation

    // Bundle products fall through to a real navigation (owner decision 2026-07-06).
    // Kaching Bundles renders a Vue widget into this section's DOM; an in-place morph
    // reconciles that DOM out from under it, leaving the bars stuck on a stale tier and
    // a mismatched price/quantity. A full page load is the only reliable reset for a
    // third-party app we don't control, so when its widget is present we do NOT hijack
    // the click. Non-bundle products keep the smooth fetch-and-swap.
    if (hasBundleWidget()) {
      // The flavor switch is the ONE interaction that must PRESERVE the viewport
      // (owner principle 2026-07-07). Bundle products can't use the in-place morph
      // (it corrupts Kaching), so they do a full reload — which scroll-container's
      // fresh-nav handler would otherwise pin to the top. Stash the current scroll
      // as a one-shot marker so scroll-container restores it on the destination.
      // Same-origin, same-tab, single-use handoff — not the cross-subdomain state
      // that CLAUDE.md rule 11 forbids.
      try {
        sessionStorage.setItem('groovi:flavorScrollPreserve', String(Math.round(window.scrollY || 0)));
      } catch (_) {}
      return;
    }

    event.preventDefault();

    // Instant click feedback (spec 2.14): the section morph takes ~0.4–1s+ with
    // NO visible change until it lands, so a flavor click reads as "nothing
    // happened." Move the active state to the clicked flavor + mark the list
    // busy the moment it's clicked, so the interaction is obviously registered.
    // The morph overwrites these with the correct server-rendered state.
    const list = link.closest('.groovi-flavor__list');
    if (list && link.classList.contains('groovi-flavor__item')) {
      for (const el of list.querySelectorAll('.groovi-flavor__item.is-active')) {
        el.classList.remove('is-active');
      }
      link.classList.add('is-active');
      list.classList.add('is-swapping');
      swap(section, href).finally(() => list.classList.remove('is-swapping'));
      return;
    }

    swap(section, href);
  });

  // Pre-warm the section cache on intent so the swap feels instant.
  // pointerenter does not bubble — capture phase still reaches document.
  document.addEventListener('pointerenter', onIntent, true);
  document.addEventListener('focusin', onIntent);

  // Back / forward: we own these history entries, so re-morph for the restored
  // URL instead of letting the browser show a stale (unswapped) section.
  window.addEventListener('popstate', () => {
    if (!/\/products\//.test(window.location.pathname)) return;
    const section = currentSection();
    if (!section || !section.id) return;
    sectionRenderer
      .renderSection(normalizeSectionId(section.id), { url: new URL(window.location.href), cache: true })
      .then(() => {
        resetQuantity(section);
        reinitMotion(section);
      })
      .catch(() => window.location.reload());
  });
}

/**
 * The live product section that hosts the flavor selector.
 * @returns {HTMLElement | null}
 */
function currentSection() {
  const flavor = document.querySelector('.groovi-flavor__item');
  return (flavor && flavor.closest('.shopify-section')) || document.querySelector('.shopify-section[id$="__main"]');
}

/**
 * Reset the swapped-in product form's quantity to 1 after a sibling morph.
 *
 * Kaching Bundles selects a tier by writing the pack count into the main
 * `input[name="quantity"]` (e.g. picking "3 Caixas" sets it to 3). The in-place
 * section morph PRESERVES live input values (morph never clobbers a field's
 * `.value`), so that stale "3" survives the swap to a different flavor — while the
 * new product only shows its single-box price and Kaching does NOT re-apply a
 * bundle. The shared live-price reflector (groovi-live-price.js) then divides the
 * new single price by the stale pack count → a badly wrong per-unit badge
 * (R$ 49,90 ÷ (3×4) = R$ 4,16 instead of R$ 12,48), frozen there across every
 * later switch. Runtime-confirmed 2026-07-06.
 *
 * Forcing the quantity back to its server default (1) after each morph makes the
 * new product start from a clean single-box state — internally consistent with the
 * price it renders — and the dispatched `change` re-runs the reflector. Kaching's
 * own widget still needs a fresh load to re-sync its highlighted tier (a
 * third-party-app / in-place-morph limitation, tracked separately), but the price
 * and per-unit badge are now correct.
 *
 * @param {HTMLElement} section
 */
function resetQuantity(section) {
  var valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
  section.querySelectorAll('input[name="quantity"]').forEach((input) => {
    if (input.value === '1') return;
    valueSetter.call(input, '1');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Re-initialize themed motion for a freshly-morphed section.
 *
 * The morph strips the `data-groovi-anim-done` marker (the server HTML never
 * carries it), so the swapped-in "Movimento" elements — e.g. the flavor
 * selector's `carimbo` entrance — sit at their FOUC-hidden `opacity: 0` state
 * (gated `.groovi-js` in groovi-custom.css) until something re-binds them.
 * Dispatching the section's own lifecycle event runs groovi-animations'
 * `scanPortedMotion` (which reveals + animates them) and `ScrollTrigger.refresh`,
 * exactly as a real Shopify section reload would. Dispatched ON the section with
 * `bubbles` so handlers that read `event.target` (sl-scroll-carousel) scope to it.
 *
 * @param {HTMLElement} section
 */
function reinitMotion(section) {
  section.dispatchEvent(
    new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId: normalizeSectionId(section.id) },
    })
  );
}

/**
 * Warm the Section Rendering HTTP cache for a sibling on hover/focus.
 * @param {Event} event
 */
function onIntent(event) {
  const link = event.target instanceof Element ? event.target.closest(LINK_SELECTOR) : null;
  if (!link) return;

  const href = link.getAttribute('href');
  const section = link.closest('.shopify-section');
  if (!href || !section || !section.id) return;

  warm(section.id, href);
}

const warmed = new Set();

/**
 * @param {string} sectionId
 * @param {string} href
 */
function warm(sectionId, href) {
  const url = new URL(href, window.location.origin);
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();
  const key = url.toString();
  if (warmed.has(key)) return;
  warmed.add(key);
  // Low-priority warm-up; matches the variant-prefetch strategy. Mirrors the
  // URL sectionRenderer builds, so its own fetch lands on the warm HTTP cache.
  fetch(key, { priority: 'low' }).catch(() => warmed.delete(key));
}

let swapping = false;

/**
 * Morph the product section to a sibling flavor without a page navigation.
 * The morph reconciles in place (smooth + instant), so no cross-fade is applied:
 * an opacity dip on the large above-the-fold product section read as the page
 * "going lighter then filling back in" and undercut how fast the swap feels.
 * @param {HTMLElement} section
 * @param {string} href
 */
async function swap(section, href) {
  if (swapping) return;
  swapping = true;

  try {
    // sectionRenderer expects the NORMALIZED id (it re-adds the `shopify-section-`
    // prefix internally). Passing the full DOM id double-prefixes it, so the
    // un-awaited morphSection fails silently while renderSection still resolves.
    await sectionRenderer.renderSection(normalizeSectionId(section.id), {
      url: new URL(href, window.location.origin),
      cache: true,
    });

    // Clear any stale bundle quantity carried over by the morph (see resetQuantity).
    resetQuantity(section);

    // Reflect the new flavor in the address bar + tab without navigating.
    history.pushState({ grooviSibling: true }, '', href);
    const title = section.querySelector('h1');
    if (title && title.textContent.trim()) document.title = title.textContent.trim();

    // Reveal + animate the swapped-in Movimento elements and refresh triggers.
    reinitMotion(section);
  } catch (error) {
    // Any failure → fall back to a real navigation so the user still gets there.
    window.location.href = href;
    return;
  } finally {
    swapping = false;
  }
}
